import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageModal from "UIPalette/PageModal/PageModal";
import { ReactComponent as CloseCircle } from "@icons/close-circle.svg";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { ResourceFilter, SupportOption } from "../../enums/supportMoments/supportMoments";
import { OutcomeIllustration } from "../OutcomeIllustration/OutcomeIllustration";

export interface PositiveOutcomeScreenProps {
  option?: SupportOption;
  resourceFilter?: ResourceFilter;
  onFinish: () => void;
  onLeave: () => void;
}

export const PositiveOutcomeScreen = ({ option, resourceFilter, onFinish, onLeave }: PositiveOutcomeScreenProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments.positiveOutcome" });
  const navigate = useNavigate();
  const { trigger: triggerLogReportingEvent } = useLogReportingEventFunctionRequest();

  useEffect(() => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_VIEWED, { value: "positive" });
  }, []);

  const handleLearnMore = () => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.learnMore") });
    navigate(`/audio-and-video/#si=${resourceFilter?.id}`, { replace: true });
    onLeave();
  };

  const handleOpenJournal = () => {
    triggerLogReportingEvent({
      body: {
        reportingEventType: ReportingEventType.SupportMomentCompleted,
        additionalInfo: option,
      },
    });

    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.openJournal") });
    navigate("/positive-you-journal", { replace: true });
    onLeave();
  };

  const handleFinish = () => {
    trackEvents(EventNames.SUPPORT_MOMENTS_OUTCOME_BUTTON_CLICK, { value: t("cta.finish") });
    onFinish();
  };

  return (
    <PageModal
      showMobileIcon={false}
      backTitle={t("cta.finish")}
      backIcon={CloseCircle}
      onClose={handleFinish}
      secondaryCta={resourceFilter ? { title: t("cta.learnMore"), onClick: handleLearnMore } : undefined}
      primaryCta={{ title: t("cta.openJournal"), onClick: handleOpenJournal }}
      primaryCtaVariant={resourceFilter ? "secondary" : "default"}
      textCta={{ title: t("cta.finish"), onClick: handleFinish }}
    >
      <div className="flex flex-col items-center text-center gap-8">
        <OutcomeIllustration testId="positive-outcome-illustration" />
        <div className="space-y-2">
          <h1>{t("heading")}</h1>
          <p>{resourceFilter ? t("descriptionWithResource", { topic: resourceFilter.title }) : t("description")}</p>
        </div>
      </div>
    </PageModal>
  );
};
